import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { notUndefined } from '@go-shared/lib/functional';
import { getDayFirstArrival, getDayLastDeparture, getDayLocation } from '@go-shared/lib/sailing';
import {
  DEFAULT_TIMESTR_FORMAT,
  getLocaleUTCDateString,
  isoDate,
  today,
} from '@go-shared/lib/time';
import { hashStringToColor } from '@go-shared/lib/utils';
import { Tooltip } from '@spark-labs/avocado-ui';
import { Dropdown, DropdownItem } from '@spark-labs/avocado-ui/dist/components/Dropdown';
import React, { useMemo } from 'react';
import {
  SailingDayDetails,
  SailingDayTemplateFragments,
  SailingDetails,
  SimpleSailing,
  SimpleSailingDay,
} from '../../shared/types/sailing';
import { BubbleBadge, BubbleButton, BubbleOverlay, BubbleTable } from './BubbleUI';
import { BubbleTableFields, BubbleTableTemplate } from './BubbleUI/BubbleTable';
import TemplateGlanceButton from './TemplateGlanceButton';
import { setStorage } from '@go-client/lib/storage';
import ItineraryNavigation from './Navigations/ItineraryNavigation';
import { resolvePlannerPath } from '@go-client/routes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getHourFormat } from '@go-shared/lib/property';
import { DayMarkersButton } from './DayMarkersButton';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canPublishDay } from '@go-shared/lib/permissions';

export interface SailingListProps {
  sailings: SimpleSailing[];
  sailingDetails: SailingDetails[];
  showAdminControls: boolean;
  currentDate?: string;
  loading?: boolean;
  onSailingDayStar?: (sailing: SimpleSailing, sailingDay: SimpleSailingDay) => void;
  onSailingDayLock?: (sailing: SimpleSailing, sailingDay: SimpleSailingDay) => void;
  onSailingDayTemplateClick?: (sailing: SimpleSailing, sailingDay: SimpleSailingDay) => void;
  onSailingDayEditClick?: (sailing: SimpleSailing, sailingDay: SimpleSailingDay) => void;
  onAction?: (action: DropdownItem, sailing: SimpleSailing) => void;
  onRundownClick?: (sailing: SimpleSailing, sailingDay: SimpleSailingDay) => void;
  onContentBuilderClick?: (sailing: SimpleSailing, sailingDay: SimpleSailingDay) => void;
  onAddDaysToBeginningClick?: () => void;
  onAddDaysToEndClick?: () => void;
  renderCustomActions?: (props: SailingListProps) => React.ReactNode;
}

interface SailingListTableItem {
  key: string;
  date: string;
  dayOfTrip: string;
  sailing: string;
  location: string;
  arrival: string;
  departure: string;
  color: string;
  templateHistory?: SailingDayTemplateFragments;
  templateFragments?: SailingDayTemplateFragments;
  isFirstDay: boolean;
  isLastDay: boolean;
  isCurrentDay: boolean;
  totalNumOfDays: number;
  _sailing: SimpleSailing;
  _sailingDay: SimpleSailingDay;
  _sailingDetails?: SailingDetails;
  _sailingDayDetails?: SailingDayDetails;
}

function hasBookingConflicts(item?: SailingDayDetails) {
  return item && item.conflicts.filter(c => c.bareGroupEvent.status === 'approved').length > 0;
}

export default function SailingList(props: SailingListProps) {
  const {
    loading,
    sailings,
    sailingDetails,
    onAction,
    onSailingDayTemplateClick,
    onSailingDayEditClick,
    onRundownClick,
    onContentBuilderClick,
    onSailingDayLock,
  } = props;

  const sailingBundles = sailings.map(s => ({
    sailing: s,
    details: sailingDetails.find(d => d.sailingId === s._id),
  }));

  const { user } = useUserInfo();
  const { activeProperty } = useActiveProperty();
  const serverInfo = useServerInfo();
  const { settings } = serverInfo;

  const hourFormat = getHourFormat(activeProperty);

  const tableItems = useMemo(() => {
    const result: SailingListTableItem[] = [];

    sailingBundles.forEach(({ sailing, details }) => {
      const color = hashStringToColor(sailing.name, true);

      sailing.days.forEach((day, indx) => {
        const isCurrentDay = (props.currentDate || today()) === isoDate(day.date);

        if (isCurrentDay) {
          setStorage(
            'currentSailingUrl',
            resolvePlannerPath(sailing.property._id, sailing._id, day._id)
          );
        }

        const sailingDayDetails = details
          ? details.dayDetails.find(d => d.sailingDayId === day._id)
          : undefined;

        let firstArrival = getDayFirstArrival(day, hourFormat, DEFAULT_TIMESTR_FORMAT);
        let lastDeparture = getDayLastDeparture(day, hourFormat, DEFAULT_TIMESTR_FORMAT);

        result.push({
          key: day._id,
          date: getLocaleUTCDateString(day.date),
          dayOfTrip: day.name,
          sailing: sailing.name,
          location: getDayLocation(day),
          color,
          arrival: firstArrival,
          departure: lastDeparture,
          templateHistory: day.templateHistory,
          templateFragments: day.templateFragments,
          isFirstDay: indx === 0,
          isLastDay: indx === sailing.days.length - 1,
          isCurrentDay,
          totalNumOfDays: sailing.days.length,
          _sailing: sailing,
          _sailingDay: day,
          _sailingDayDetails: sailingDayDetails,
        });
      });
    });

    return result;
  }, [sailingBundles]);

  const sailingLabel = getPropertyConfiguration(activeProperty.type, 'sailingLabel');

  const tableFields: Array<BubbleTableFields<SailingListTableItem>> = [
    {
      key: 'sailing',
      label: sailingLabel,
    },
    settings.PUBLISHER_ROLE !== 'inactive'
      ? {
          key: 'published',
          label: '',
          thProps: { className: 'w-0' },
          tdProps: { className: 'w-0 p-1-i pt-2-i pl-3-i' },
        }
      : undefined,
    {
      key: 'date',
    },
    {
      key: 'dayOfTrip',
      label: 'Day',
    },
    activeProperty.type === 'sea'
      ? {
          key: 'location',
        }
      : undefined,
    activeProperty.type === 'sea'
      ? {
          key: 'arrival',
        }
      : undefined,
    activeProperty.type === 'sea'
      ? {
          key: 'departure',
        }
      : undefined,
    {
      key: 'templates',
      thProps: { className: 'pl-1-i' },
      tdProps: { className: 'p-1-i pt-2-i' },
    },
    {
      key: 'markers',
      thProps: { className: 'pl-1-i' },
      tdProps: { className: 'p-1-i' },
    },
    {
      key: 'actions',
      label: '',
      thProps: { className: 'pl-4-i' },
      tdProps: { className: 'whitespace-nowrap p-1-i pr-4-i text-right' },
    },
  ].filter(notUndefined);

  const canPublish = canPublishDay(user, serverInfo);

  const dropdownItems = [
    { key: 'apply', element: 'Template Summary' },
    canPublish ? { key: 'publish', element: `Publish ${sailingLabel}` } : undefined,
    { key: 'voyage-csv', element: `Export ${sailingLabel}` },
    { key: 'voyage-csv-2', element: `Export ${sailingLabel} (Format 2)` },
  ].filter(notUndefined);

  function makeDefaultTemplateTdProps(
    data: SailingListTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const { isLastDay, isCurrentDay, color } = data;

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        isLastDay ? 'border-b-2' : undefined,
        isCurrentDay ? 'bg-selected' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
      style: {
        borderBottomColor: isLastDay ? color : '',
      },
    };
  }

  const tableTemplates: Array<BubbleTableTemplate<SailingListTableItem>> = [
    {
      key: 'sailing',
      render: ({ isFirstDay, sailing, color, _sailing }) =>
        isFirstDay ? (
          <>
            <div className="mb-3">
              {sailing.split(' ').map((s, i) => (
                <div key={i}>{s}</div>
              ))}
            </div>
            <div className="dropdown-inline-fix">
              <Dropdown
                items={dropdownItems}
                onSelect={onAction ? action => onAction(action, _sailing) : undefined}
              >
                {({ toggleRef, ...rest }) => (
                  <BubbleButton
                    buttonRef={toggleRef}
                    {...rest}
                    variant="white"
                    className="shadow py-1-i"
                    style={{ color }}
                    icon="bars"
                  />
                )}
              </Dropdown>
            </div>
          </>
        ) : (
          <></>
        ),
      tdProps: ({ isFirstDay, totalNumOfDays, color }) =>
        isFirstDay
          ? {
              rowSpan: totalNumOfDays,
              className: 'border-b-2 align-top text-md font-semibold text-white relative',
              width: '120px',
              style: {
                backgroundColor: color,
                borderColor: color,
              },
            }
          : { style: { display: 'none' } },
    },
    {
      key: 'published',
      render: item => (
        <BubbleBadge
          showIcon
          iconName={item._sailingDayDetails?.published ? 'paper-plane' : 'pencil'}
          variant={item._sailingDayDetails?.published ? 'emerald' : 'dirt'}
        ></BubbleBadge>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'date',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'dayOfTrip',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'voyage',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'location',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'arrival',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'departure',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'templates',
      render: item => (
        <TemplateGlanceButton
          locked={item._sailingDay.locked}
          className="text-xs"
          onClick={
            onSailingDayTemplateClick
              ? () => onSailingDayTemplateClick(item._sailing, item._sailingDay)
              : undefined
          }
          templateHistory={item.templateHistory}
          templateFragments={item.templateFragments}
        />
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'markers',
      render: item => <DayMarkersButton sailingDay={item._sailingDay} />,
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'actions',
      render: item => (
        <>
          <BubbleButton
            className="p-2-i"
            variant="navy"
            inverse
            onClick={
              onRundownClick ? () => onRundownClick(item._sailing, item._sailingDay) : undefined
            }
            icon={['fass', 'chart-tree-map']}
          />
          <BubbleButton
            className="p-2-i mr-2"
            variant="navy"
            inverse
            onClick={
              onContentBuilderClick
                ? () => onContentBuilderClick(item._sailing, item._sailingDay)
                : undefined
            }
            icon={['fass', 'newspaper']}
          />

          <BubbleButton
            className="p-2-i"
            variant="slate"
            inverse
            onClick={
              onSailingDayEditClick
                ? () => onSailingDayEditClick(item._sailing, item._sailingDay)
                : undefined
            }
            icon="edit"
          />
          <BubbleButton
            className="p-2-i"
            variant={item._sailingDay.locked ? 'crimson' : 'smoke'}
            inverse
            disabled={!props.showAdminControls}
            onClick={
              onSailingDayLock ? () => onSailingDayLock(item._sailing, item._sailingDay) : undefined
            }
            icon={item._sailingDay.locked ? 'lock' : 'unlock'}
          />
          {hasBookingConflicts(item._sailingDayDetails) && (
            <Tooltip
              position="left"
              text="There are booking conflicts"
              className="whitespace-normal"
            >
              <span className="p-2-i">
                <FontAwesomeIcon fixedWidth icon="user-group" className="text-purple-darker" />
              </span>
            </Tooltip>
          )}
        </>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
  ];

  const DefaultActions = () => (
    <div className="w-full flex justify-center -mx-1">
      <BubbleButton variant="slate" className="mx-1" onClick={props.onAddDaysToBeginningClick}>
        Load Days Before
      </BubbleButton>
      <BubbleButton className="mx-1" variant="navy" onClick={props.onAddDaysToEndClick}>
        Load Days After
      </BubbleButton>
    </div>
  );

  return (
    <>
      <div
        className="ItineraryHeader align-with-logo flex items-end
      px-5 sticky top-0 left-0 z-10 bg-blue-lightest"
      >
        <div className="mb-3 pl-3 flex-grow flex items-end">
          <ItineraryNavigation active="itinerary" />
        </div>
      </div>

      <div className="ItineraryBody mx-5 content-minus-logo min-w-900">
        <BubbleOverlay loading={loading}>
          <BubbleTable
            fields={tableFields}
            items={tableItems}
            templates={tableTemplates}
            onRowDblClick={item => {
              onSailingDayEditClick && onSailingDayEditClick(item._sailing, item._sailingDay);
            }}
            stickyHeader={130}
            headVariant="navy"
            responsive
            striped
          />
        </BubbleOverlay>
        {props.renderCustomActions ? props.renderCustomActions(props) : <DefaultActions />}
        <div className="mt-3">&nbsp;</div>
      </div>
    </>
  );
}
